import { createContext, useContext, useMemo, type ReactNode } from "react";
import { MockFeedService } from "./mock/feedService";
import { MockAcademicService, MockProfessorService } from "./mock/academicService";
import {
  MockChallengeService,
  MockEventService,
  MockMarketplaceService,
  MockMediaService,
  MockRewardService,
} from "./mock/engagementServices";
import {
  MockAuthService,
  MockHoroscopeService,
  MockMessagingService,
} from "./mock/identityServices";
import { MockMatchingService } from "./mock/matchingService";

/**
 * Dependency injection, mirroring the iOS app's `AppState`.
 *
 * Every service is constructed once and read through context — there are no
 * ViewModels on either side of the port; views call services directly. Keeping
 * that convention means a screen reads the same way in Swift and here.
 *
 * The instances are stateful: likes, RSVPs, joins and applications are written
 * back into each service's store, so an interaction survives navigation for the
 * life of the session. That is exactly as long as a demo lasts, which is the
 * whole requirement.
 */
export interface Services {
  feed: MockFeedService;
  academic: MockAcademicService;
  professor: MockProfessorService;
  matching: MockMatchingService;
  messaging: MockMessagingService;
  horoscope: MockHoroscopeService;
  auth: MockAuthService;
  challenge: MockChallengeService;
  reward: MockRewardService;
  marketplace: MockMarketplaceService;
  event: MockEventService;
  media: MockMediaService;
}

export function createServices(): Services {
  return {
    feed: new MockFeedService(),
    academic: new MockAcademicService(),
    professor: new MockProfessorService(),
    matching: new MockMatchingService(),
    messaging: new MockMessagingService(),
    horoscope: new MockHoroscopeService(),
    auth: new MockAuthService(),
    challenge: new MockChallengeService(),
    reward: new MockRewardService(),
    marketplace: new MockMarketplaceService(),
    event: new MockEventService(),
    media: new MockMediaService(),
  };
}

const ServicesContext = createContext<Services | null>(null);

export function ServicesProvider({ children }: { children: ReactNode }) {
  // Constructed once. Re-running this would re-seed every store and silently
  // discard whatever the user has liked or joined.
  const services = useMemo(createServices, []);
  return <ServicesContext.Provider value={services}>{children}</ServicesContext.Provider>;
}

export function useServices(): Services {
  const services = useContext(ServicesContext);
  if (!services) throw new Error("useServices called outside ServicesProvider");
  return services;
}
